import React, { useMemo, useLayoutEffect, useRef, useReducer } from "react";

import styles, {
  lightbox,
  image,
  captionContainer,
  captionText,
  lightboxInner,
  enabled,
  imageWrapper,
} from "./LightBox.module.css";

import getRenderedSize from "./getRenderedSize";

const stages = {
  initial: Symbol("initial"),
  loading: Symbol("loading"),
  loaded: Symbol("loaded"),
  beforeAnimateOpen: Symbol("beforeAnimateOpen"),
  animatingOpen: Symbol("animatingOpen"),
  opened: Symbol("opened"),
  animatingClosed: Symbol("animatingClosed"),
};

const initialState = {
  stage: stages.initial,
  imageSrc: undefined,
  sourceDim: undefined,
  windowScroll: undefined,
  destDim: undefined,
  transformData: undefined,
  transform: undefined,
  viewport: undefined,
  destMax: undefined,
  transition: undefined,
  caption: "",
  mood: undefined,
  imageOpacity: 0,
  containerOpacity: 0,
  containX: undefined,
  offsetX: undefined,
  destHeight: undefined,
  destWidth: undefined,
};

const reducer = (state, { type, payload }) => {
  switch (type) {
    case "startLoad": {
      const {
        imageSrc,
        viewportWidth,
        viewportHeight,
        sourceHeight,
        sourceWidth,
        sourceTop,
        sourceLeft,
        scrollTop,
        scrollLeft,
        caption,
        mood,
      } = payload;
      return {
        ...state,
        stage: stages.loading,
        imageSrc,
        viewport: {
          width: viewportWidth,
          height: viewportHeight,
        },
        sourceDim: {
          sourceHeight,
          sourceWidth,
          sourceTop,
          sourceLeft,
          scrollTop,
          scrollLeft,
        },
        windowScroll: {
          scrollTop,
          scrollLeft,
        },
        caption,
        mood,
      };
    }
    case "endLoad": {
      const { maxHeight, maxWidth } = payload;
      return {
        ...state,
        stage: stages.loaded,
        destMax: {
          maxHeight,
          maxWidth,
        },
      };
    }
    case "initAnimateOpen": {
      const {
        destHeight,
        destWidth,
        destTop,
        destLeft,
        scaleX,
        scaleY,
        translateX,
        translateY,
        containX,
        offsetX,
      } = payload;

      const transformString = `translate(${translateX || 0}px, ${
        translateY || 0
      }px) scale(${(scaleX, scaleY)})`;

      return {
        ...state,
        stage: stages.beforeAnimateOpen,
        destDim: {
          destHeight,
          destWidth,
          destTop,
          destLeft,
        },
        transformData: {
          scaleX,
          scaleY,
          translateX,
          translateY,
          transformString,
        },
        transform: transformString,
        imageOpacity: 1,
        containX,
        offsetX,
      };
    }
    case "triggerAnimateOpen": {
      return {
        ...state,
        stage: stages.animatingOpen,
        transform: undefined,
        transition: "transform 0.25s cubic-bezier(0.2, 0, 0.1, 1)",
        containerOpacity: 0.8,
      };
    }
    case "finishAnimationOpen": {
      return {
        ...state,
        stage: stages.opened,
        transition: undefined,
      };
    }
    case "triggerAnimationClosed": {
      return {
        ...state,
        stage: stages.animatingClosed,
        transform: state.transformData && state.transformData.transformString,
        containerOpacity: 0,
        transition: "transform 0.25s cubic-bezier(0.2, 0, 0.1, 1)",
      };
    }
    case "reset": {
      return initialState;
    }
    default: {
      throw new Error();
    }
  }
};

const LightBox = (props) => {
  const { src, imageDimensions, sourceImageRef } = props;
  const [state, dispatch] = useReducer(reducer, initialState);
  const imageRef = useRef(undefined);
  const containerRef = useRef(undefined);
  const { stage, caption, mood } = state;

  useLayoutEffect(() => {
    if (src && imageDimensions) {
      if (stage !== state.initial) {
        dispatch({ type: "reset" });
      }
      const { clientWidth: viewportWidth, clientHeight: viewportHeight } =
        document.documentElement;
      const {
        height: sourceHeight,
        width: sourceWidth,
        top: sourceTop,
        left: sourceLeft,
      } = imageDimensions;
      const scrollTop = window.scrollY;
      const scrollLeft = window.scrollX;
      const { caption, mood } = props;

      dispatch({
        type: "startLoad",
        payload: {
          imageSrc: src,
          viewportWidth,
          viewportHeight,
          sourceHeight,
          sourceWidth,
          sourceTop,
          sourceLeft,
          scrollTop,
          scrollLeft,
          caption,
          mood,
        },
      });
    } else if (!src && stage !== stages.initial) {
      dispatch({ type: "triggerAnimationClosed" });
    }
  }, [src, imageDimensions]);

  const handleImageLoad = () => {
    if (state.imageSrc) {
      const { naturalWidth: nativeWidth, naturalHeight: nativeHeight } =
        imageRef.current;
      const { width: viewportWidth, height: viewportHeight } = state.viewport;

      dispatch({
        type: "endLoad",
        payload: {
          maxWidth: nativeWidth > viewportWidth ? viewportWidth : nativeWidth,
          maxHeight:
            nativeHeight > viewportHeight ? viewportHeight : nativeHeight,
        },
      });
    }
  };

  const captionPosition = useMemo(() => {
    if (!state.viewport || !state.destMax) return;
    const { maxWidth: xi, maxHeight: yi } = state.destMax;
    const { width: xv, height: yv } = state.viewport;

    const scaleX = xv / xi;

    if (yi * (scaleX * 0.8) > yv) return "side";

    return "bottom";
  }, [state.viewport, state.destMax]);

  useLayoutEffect(() => {
    if (state.destMax && stage === stages.loaded && captionPosition) {
      const {
        height: sourceHeight,
        width: sourceWidth,
        top: sourceTop,
        left: sourceLeft,
      } = sourceImageRef.current.getBoundingClientRect();

      const {
        width: containWidth,
        height: containHeight,
        top: containTop,
        left: containLeft,
      } = containerRef.current.getBoundingClientRect();

      const { naturalHeight: maxHeight, naturalWidth: maxWidth } =
        imageRef.current;

      let { height: destHeight, width: destWidth } = getRenderedSize(
        true,
        containWidth,
        containHeight,
        maxWidth,
        maxHeight
      );

      if (destHeight > maxHeight || destWidth > maxWidth) {
        destHeight = maxHeight;
        destWidth = maxWidth;
      }

      const sourceCenter = {
        x: sourceLeft + window.pageXOffset + sourceWidth / 2,
        y: sourceTop + window.pageYOffset + sourceHeight / 2,
      };

      const destCenter = {
        x:
          captionPosition === "bottom"
            ? containLeft +
              window.pageXOffset +
              destWidth / 2 +
              (containWidth - destWidth) / 2
            : containLeft + window.pageXOffset + containWidth - destWidth / 2,
        y:
          containTop +
          window.pageYOffset +
          destHeight / 2 +
          (containHeight - destHeight) * 0.5,
      };

      const scaleX = sourceWidth / destWidth;
      const scaleY = sourceHeight / destHeight;

      const translateX = sourceCenter.x - destCenter.x;
      const translateY = sourceCenter.y - destCenter.y;

      const containX = destWidth < 800 ? 0 : destWidth - 800;

      requestAnimationFrame(() => {
        dispatch({
          type: "initAnimateOpen",
          payload: {
            destHeight,
            destWidth,
            destTop: containTop,
            destLeft: containLeft,
            scaleX,
            scaleY,
            translateX,
            translateY,
            containX,
          },
        });
      });
    }
  }, [
    state.destMax,
    stage === stages.loaded,
    imageRef,
    sourceImageRef,
    captionPosition,
  ]);

  useLayoutEffect(() => {
    if (state.transformData && stage === stages.beforeAnimateOpen) {
      requestAnimationFrame(() => {
        sourceImageRef.current.style.visibility = "hidden";
        dispatch({ type: "triggerAnimateOpen" });
      });
    }
  }, [state.transformData, stage === stages.beforeAnimateOpen]);

  const handleTransitionEnd = () => {
    if (stage === stages.animatingOpen) {
      dispatch({ type: "finishAnimationOpen" });
    }

    if (stage === stages.animatingClosed) {
      sourceImageRef.current.style.visibility = "visible";
      dispatch({ type: "reset" });
    }
  };

  const outerPostion = state.windowScroll
    ? { top: state.windowScroll.scrollTop, left: state.windowScroll.scrollLeft }
    : {};

  const imageTransforms = {
    transform: state.transform,
    transition: state.transition,
    opacity: state.imageOpacity,
    height: state.destDim && `${state.destDim.destHeight}px`,
    width: state.destDim && `${state.destDim.destWidth}px`,
    // width: 'auto',
  };

  return (
    <div
      className={`${lightbox} ${state.stage !== stages.initial && enabled}`}
      style={{
        ...outerPostion,
        background: `rgba(255, 255, 255, ${state.containerOpacity})`,
      }}
    >
      <div className={`${lightboxInner} ${styles[captionPosition]}`}>
        <div
          className={imageWrapper}
          ref={containerRef}
          style={
            state.caption
              ? {
                  justifyContent:
                    caption && captionPosition === "side" && "flex-end",
                }
              : {
                  maxWidth: "100%",
                  maxHeight: "100%",
                }
          }
        >
          <img
            src={state.imageSrc}
            onLoad={handleImageLoad}
            className={image}
            ref={imageRef}
            style={imageTransforms}
            onTransitionEnd={handleTransitionEnd}
          />
        </div>
        {caption && (
          <div
            className={captionContainer}
            style={{
              opacity: state.containerOpacity ? 1 : 0,
              justifyContent:
                caption && captionPosition === "side" && "flex-start",
              marginLeft:
                captionPosition === "bottom" ? `-${state.containX}px` : 0,
            }}
          >
            <p
              className={`${captionText} ${mood && styles[mood]}`}
              style={{
                ...props.captionStyle,
              }}
              dangerouslySetInnerHTML={{ __html: caption }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default LightBox;
